import { Fragment, useEffect, useRef } from 'react';
import { AnnotationDetailsPanel } from './AnnotationDetailsPanel.jsx';
import {
  annotationDisplayNumber,
  annotationSummaryText,
  getDisplayOrderedAnnotations,
} from './feedbackAnnotations.js';
import { SEVERITY_OPTIONS } from './AnnotationDetailsPanel.jsx';
import {
  annotationKindLabel,
  colorIdForValue,
  colorLabelForValue,
  FEEDBACK_COLORS,
} from './feedbackTypes.js';

function kindLabel(annotation) {
  return annotationKindLabel(annotation?.type);
}

export function AnnotationListPanel({
  annotations = [],
  actorUserId = null,
  selectedAnnotationId = null,
  readOnly = false,
  onSelect,
  onUpdate,
  onDelete,
  onColorChange,
  suppressSelectionFocus = false,
}) {
  const cardRefs = useRef(new Map());
  const headerRefs = useRef(new Map());
  // Read through a ref so the focus effect keeps firing only on selection
  // changes — suppress toggling must not itself move focus.
  const suppressFocusRef = useRef(suppressSelectionFocus);
  suppressFocusRef.current = suppressSelectionFocus;
  const orderedAnnotations = getDisplayOrderedAnnotations(annotations);
  const selectedIndex = orderedAnnotations.findIndex(
    (annotation) => annotation.id === selectedAnnotationId,
  );
  const selectedAnnotation = selectedIndex >= 0 ? orderedAnnotations[selectedIndex] : null;

  useEffect(() => {
    const selectedId = selectedAnnotation?.id;
    if (!selectedId) return;
    const card = cardRefs.current.get(selectedId);
    const header = headerRefs.current.get(selectedId);
    card?.scrollIntoView?.({ block: 'nearest' });
    // Placing a text callout selects it AND opens the inline stage editor in
    // the same commit; stealing focus here would blur the editor and close it
    // instantly (onBlur={stopTextEditing}). Keep the card in view, leave focus.
    if (!suppressFocusRef.current) header?.focus?.({ preventScroll: true });
  }, [selectedAnnotation?.id]);

  if (!orderedAnnotations.length) {
    return (
      <div className="fbws-panel-empty">
        <span className="fbws-panel-empty-ic" aria-hidden>
          ✎
        </span>
        <b>Nothing selected yet</b>
        <p>
          Drop a pin, box, arrow, highlight, or pen mark on the screenshot — this editor opens
          automatically so you can say what you want in your own words.
        </p>
      </div>
    );
  }

  return (
    <div className="feedback-annotation-list" data-testid="feedback-annotation-list">
      {selectedIndex < 0 ? (
        <div className="feedback-annotation-card-nav" role="group" aria-label="Mark navigation">
          <span>No mark selected</span>
          <button type="button" disabled>
            Previous mark
          </button>
          <button type="button" onClick={() => onSelect?.(orderedAnnotations[0].id)}>
            Next mark
          </button>
        </div>
      ) : null}
      {orderedAnnotations.map((annotation) => {
        const number = annotationDisplayNumber(annotation.id);
        const expanded = annotation.id === selectedAnnotation?.id;
        const selectedColorId = colorIdForValue(annotation?.style?.strokeColor);
        return (
          <section
            key={annotation.id}
            className={`feedback-annotation-card ${expanded ? 'selected' : ''}`}
            data-testid={`feedback-annotation-card-${annotation.id}`}
            aria-current={expanded ? 'true' : undefined}
            ref={(node) => {
              if (node) cardRefs.current.set(annotation.id, node);
              else cardRefs.current.delete(annotation.id);
            }}
          >
            <button
              type="button"
              className="feedback-annotation-card-header"
              data-testid={`feedback-annotation-card-toggle-${annotation.id}`}
              aria-current={expanded ? 'true' : undefined}
              aria-expanded={expanded}
              onClick={() => onSelect?.(annotation.id)}
              ref={(node) => {
                if (node) headerRefs.current.set(annotation.id, node);
                else headerRefs.current.delete(annotation.id);
              }}
            >
              <span>{`Mark ${number}`}</span>
              <span>{kindLabel(annotation)}</span>
              {!expanded ? <span>{annotationSummaryText(annotation)}</span> : null}
            </button>

            {expanded ? (
              <div className="feedback-annotation-card-body">
                <div
                  className="feedback-annotation-card-nav"
                  role="group"
                  aria-label="Mark navigation"
                >
                  <button
                    type="button"
                    data-testid="feedback-annotation-prev"
                    onClick={() =>
                      selectedIndex > 0 && onSelect?.(orderedAnnotations[selectedIndex - 1].id)
                    }
                    disabled={selectedIndex <= 0}
                  >
                    Previous mark
                  </button>
                  <button
                    type="button"
                    data-testid="feedback-annotation-next"
                    onClick={() =>
                      selectedIndex >= 0 &&
                      selectedIndex < orderedAnnotations.length - 1 &&
                      onSelect?.(orderedAnnotations[selectedIndex + 1].id)
                    }
                    disabled={selectedIndex < 0 || selectedIndex >= orderedAnnotations.length - 1}
                  >
                    Next mark
                  </button>
                </div>

                {readOnly ? (
                  <ReadOnlyAnnotationDetails
                    annotation={annotation}
                  />
                ) : (
                  <Fragment key={`editable-${annotation.id}`}>
                    <div
                      className="feedback-annotation-color-row"
                      role="group"
                      aria-label="Annotation colors"
                    >
                      {FEEDBACK_COLORS.map((color) => (
                        <button
                          key={color.id}
                          type="button"
                          className={`feedback-swatch ${selectedColorId === color.id ? 'active' : ''}`}
                          style={{ '--swatch': color.value }}
                          data-testid={`feedback-annotation-color-${annotation.id}-${color.id}`}
                          aria-label={color.label}
                          aria-pressed={selectedColorId === color.id}
                          onClick={() => onColorChange?.(annotation.id, color.id)}
                        />
                      ))}
                    </div>

                    <AnnotationDetailsPanel
                      annotation={annotation}
                      actorUserId={actorUserId}
                      onUpdate={onUpdate}
                      onDelete={() => onDelete?.(annotation.id)}
                      autoFocusNote={false}
                      noteTestId={`feedback-annotation-note-${annotation.id}`}
                      dictateTestId={`feedback-annotation-dictate-${annotation.id}`}
                      dictationStatusTestId={`feedback-annotation-dictation-status-${annotation.id}`}
                    />
                  </Fragment>
                )}
              </div>
            ) : null}
          </section>
        );
      })}
    </div>
  );
}

function ReadOnlyAnnotationDetails({ annotation }) {
  const draft = annotation?.feedbackDraft || {};
  const severityLabel =
    SEVERITY_OPTIONS.find((option) => option.value === draft.severity)?.label || 'Not set';
  const colorLabel = colorLabelForValue(annotation?.style?.strokeColor);

  return (
    <div className="feedback-details-panel" data-testid="feedback-details-panel">
      <div className="feedback-field">
        <span className="feedback-primary-question-label">What would you like changed?</span>
        <p>{annotation?.instruction || draft.description || 'No feedback text yet.'}</p>
      </div>

      <div className="feedback-field">
        <span className="feedback-severity-label">Importance</span>
        <p>{severityLabel}</p>
      </div>

      <div className="feedback-field">
        <span className="feedback-severity-label">Color</span>
        <p>{colorLabel}</p>
      </div>

      {draft.actualBehavior ? (
        <div className="feedback-field">
          <span>What happens today?</span>
          <p>{draft.actualBehavior}</p>
        </div>
      ) : null}

      {draft.expectedBehavior ? (
        <div className="feedback-field">
          <span>What should happen instead?</span>
          <p>{draft.expectedBehavior}</p>
        </div>
      ) : null}
    </div>
  );
}
